// @flow
import update from 'immutability-helper';
import { of } from 'rxjs';
import { map, catchError, mergeMap } from 'rxjs/operators';
import { getCountriesApi, getItemCategoriesApi, getStoreCategoriesApi, getOnlineStores } from './api';
import { populateCategories, indexCategoriesById } from './utils/categories';
import { DEFAULT_COUNTRIES, populateCountries, indexCountriesById } from './utils/countries';
import { indexStoreCategoriesById } from './utils/store-categories';
import { ofType } from 'redux-observable';

import type { Observable } from 'rxjs';
import type { HTTPError } from '../../error';
import type { Category, CountryFull, OnlineStore, OnlineStoreCategory } from '../../entities';
import type { LocalValueCountry } from './utils/countries';

type LocalValuesAction = {
  +type: string;
  +error?: HTTPError;
  +categories?: Category[];
  +categoriesMap?: { [key: string]: Category };
  +countries?: CountryFull[];
  +countriesMap?: { [key: number]: LocalValueCountry };
  +storeCategories?: OnlineStoreCategory[];
  +storeCategoriesMap?: { [key: number]: OnlineStoreCategory };
  +onlineStores?: OnlineStore[];
  +fetchOnlineStoresError?: HTTPError;
};

export type LocalValuesState = {
  +isFetchingCategories?: boolean;
  +categories?: Category[];
  +categoriesMap?: { [key: string]: Category };

  +fetchCategoriesError?: HTTPError;
  +isFetchingCountries?: boolean;
  +countries?: LocalValueCountry[];
  +countriesMap?: { [key: string]: LocalValueCountry };
  +fetchCountriesError?: HTTPError;

  +isFetchingStoreCategories?: boolean;
  +storeCategories?: OnlineStoreCategory[];
  +storeCategoriesMap?: { [key: number]: OnlineStoreCategory };
  +fetchStoreCategoriesError?: HTTPError;

  +isFetchingOnlineStores?: boolean;
  +onlineStores?: OnlineStore[];
  +fetchOnlineStoresError?: HTTPError;
};

// Actions
const LOCAL_VALUES_FETCH_CATEGORIES_REQUEST = 'LOCAL_VALUES_FETCH_CATEGORIES_REQUEST';
const LOCAL_VALUES_FETCH_CATEGORIES_SUCCESS = 'LOCAL_VALUES_FETCH_CATEGORIES_SUCCESS';
const LOCAL_VALUES_FETCH_CATEGORIES_ERROR = 'LOCAL_VALUES_FETCH_CATEGORIES_ERROR';

const LOCAL_VALUES_FETCH_COUNTRIES_REQUEST = 'LOCAL_VALUES_FETCH_COUNTRIES_REQUEST';
const LOCAL_VALUES_FETCH_COUNTRIES_SUCCESS = 'LOCAL_VALUES_FETCH_COUNTRIES_SUCCESS';
const LOCAL_VALUES_FETCH_COUNTRIES_ERROR = 'LOCAL_VALUES_FETCH_COUNTRIES_ERROR';

const LOCAL_VALUES_FETCH_STORE_CATEGORIES_REQUEST = 'LOCAL_VALUES_FETCH_STORE_CATEGORIES_REQUEST';
const LOCAL_VALUES_FETCH_STORE_CATEGORIES_SUCCESS = 'LOCAL_VALUES_FETCH_STORE_CATEGORIES_SUCCESS';
const LOCAL_VALUES_FETCH_STORE_CATEGORIES_ERROR = 'LOCAL_VALUES_FETCH_STORE_CATEGORIES_ERROR';

const LOCAL_VALUES_ONLINE_STORES_REQUEST = 'LOCAL_VALUES_ONLINE_STORES_REQUEST';
const LOCAL_VALUES_ONLINE_STORES_SUCCESS = 'LOCAL_VALUES_ONLINE_STORES_SUCCESS';
const LOCAL_VALUES_ONLINE_STORES_ERROR = 'LOCAL_VALUES_ONLINE_STORES_ERROR';

// Action Creators
export function fetchCategories(): LocalValuesAction {
  return {
    type: LOCAL_VALUES_FETCH_CATEGORIES_REQUEST,
  };
}

export function fetchCategoriesSuccess(categories: Category[]): LocalValuesAction {
  return {
    type: LOCAL_VALUES_FETCH_CATEGORIES_SUCCESS,
    categories,
  };
}

export function fetchCategoriesError(error: HTTPError): LocalValuesAction {
  return {
    type: LOCAL_VALUES_FETCH_CATEGORIES_ERROR,
    fetchCategoriesError: error,
  };
}

export function fetchCountries(): LocalValuesAction {
  return {
    type: LOCAL_VALUES_FETCH_COUNTRIES_REQUEST,
  };
}

export function fetchCountriesSuccess(countries: CountryFull[]): LocalValuesAction {
  return {
    type: LOCAL_VALUES_FETCH_COUNTRIES_SUCCESS,
    countries,
  };
}

export function fetchCountriesError(error: HTTPError): LocalValuesAction {
  return {
    type: LOCAL_VALUES_FETCH_COUNTRIES_ERROR,
    fetchCountriesError: error,
  };
}

export function fetchStoreCategories(): LocalValuesAction {
  return {
    type: LOCAL_VALUES_FETCH_STORE_CATEGORIES_REQUEST,
  };
}

export function fetchStoreCategoriesSuccess(storeCategories: OnlineStoreCategory[]): LocalValuesAction {
  return {
    type: LOCAL_VALUES_FETCH_STORE_CATEGORIES_SUCCESS,
    storeCategories,
  };
}

export function fetchStoreCategoriesError(error: HTTPError): LocalValuesAction {
  return {
    type: LOCAL_VALUES_FETCH_STORE_CATEGORIES_ERROR,
    fetchStoreCategoriesError: error,
  };
}

export function fetchOnlineStores(): LocalValuesAction {
  return {
    type: LOCAL_VALUES_ONLINE_STORES_REQUEST,
  };
}

export function fetchOnlineStoresSuccess(onlineStores: OnlineStore[]): LocalValuesAction {
  return {
    type: LOCAL_VALUES_ONLINE_STORES_SUCCESS,
    onlineStores,
  };
}

export function fetchOnlineStoresErrorFn(fetchOnlineStoresError: HTTPError): LocalValuesAction {
  return {
    type: LOCAL_VALUES_ONLINE_STORES_ERROR,
    fetchOnlineStoresError,
  }
}

export const actions = {
  LOCAL_VALUES_FETCH_CATEGORIES_REQUEST,
  LOCAL_VALUES_FETCH_CATEGORIES_SUCCESS,
  LOCAL_VALUES_FETCH_CATEGORIES_ERROR,
  LOCAL_VALUES_FETCH_COUNTRIES_REQUEST,
  LOCAL_VALUES_FETCH_COUNTRIES_SUCCESS,
  LOCAL_VALUES_FETCH_COUNTRIES_ERROR,
  LOCAL_VALUES_FETCH_STORE_CATEGORIES_REQUEST,
  LOCAL_VALUES_FETCH_STORE_CATEGORIES_SUCCESS,
  LOCAL_VALUES_FETCH_STORE_CATEGORIES_ERROR,
  LOCAL_VALUES_ONLINE_STORES_REQUEST,
  LOCAL_VALUES_ONLINE_STORES_SUCCESS,
  LOCAL_VALUES_ONLINE_STORES_ERROR,
  fetchCategories,
  fetchCategoriesSuccess,
  fetchCategoriesError,
  fetchCountries,
  fetchCountriesSuccess,
  fetchCountriesError,
  fetchStoreCategories,
  fetchStoreCategoriesSuccess,
  fetchStoreCategoriesError,
  fetchOnlineStores,
  fetchOnlineStoresSuccess,
  fetchOnlineStoresErrorFn,
};

export const initialState: LocalValuesState = {};

// Reducer
export default function reducer(state: LocalValuesState = initialState,
                                action: LocalValuesAction): LocalValuesState {
  switch (action.type) {
    case LOCAL_VALUES_FETCH_CATEGORIES_REQUEST:
      return update(state, {
        isFetchingCategories: { $set: true },
      });
    case LOCAL_VALUES_FETCH_CATEGORIES_SUCCESS:
      const populatedCategories = populateCategories(action.categories || []);
      const categoriesMap = indexCategoriesById(populatedCategories);
      return update(state, {
        $unset: ['isFetchingCategories'],
        categories: { $set: populatedCategories },
        categoriesMap: { $set: categoriesMap },
      });
    case LOCAL_VALUES_FETCH_CATEGORIES_ERROR:
      return update(state, {
        $unset: ['isFetchingCategories'],
        fetchCategoriesError: { $set: action.error },
      });
    case LOCAL_VALUES_FETCH_COUNTRIES_REQUEST:
      return update(state, {
        isFetchingCountries: { $set: true },
      });
    case LOCAL_VALUES_FETCH_COUNTRIES_SUCCESS:
      const populatedCountries = populateCountries(action.countries || []);
      const countriesMap = indexCountriesById(populatedCountries);
      return update(state, {
        $unset: ['isFetchingCountries'],
        countries: { $set: populatedCountries },
        countriesMap: { $set: countriesMap },
      });
    case LOCAL_VALUES_FETCH_COUNTRIES_ERROR:
      const defaultCountriesMap = indexCountriesById(DEFAULT_COUNTRIES);
      return update(state, {
        $unset: ['isFetchingCountries'],
        fetchCountriesError: { $set: action.error },
        countries: { $set: DEFAULT_COUNTRIES },
        countriesMap: { $set: defaultCountriesMap },
      });
    case LOCAL_VALUES_FETCH_STORE_CATEGORIES_REQUEST:
      return update(state, {
        isFetchingStoreCategories: { $set: true },
      });
    case LOCAL_VALUES_FETCH_STORE_CATEGORIES_SUCCESS:
      // const populatedCategories = populateCategories(action.categories || []);
      const storeCategoriesMap = indexStoreCategoriesById(action.storeCategories || []);
      return update(state, {
        $unset: ['isFetchingStoreCategories'],
        storeCategories: { $set: action.storeCategories },
        storeCategoriesMap: { $set: storeCategoriesMap },
      });
    case LOCAL_VALUES_FETCH_STORE_CATEGORIES_ERROR:
      return update(state, {
        $unset: ['isFetchingStoreCategories'],
        fetchStoreCategoriesError: { $set: action.error },
      });
    case LOCAL_VALUES_ONLINE_STORES_REQUEST:
      return update(state, {
        isFetchingOnlineStores: { $set: true },
      });
    case LOCAL_VALUES_ONLINE_STORES_SUCCESS:
      return update(state, {
        $unset: ['isFetchingOnlineStores'],
        onlineStores: { $set: action.onlineStores },
      });
    case LOCAL_VALUES_ONLINE_STORES_ERROR:
      return update(state, {
        $unset: ['isFetchingOnlineStores'],
        fetchOnlineStoresError: { $set: action.fetchOnlineStoresError },
      });
    default:
      return state;
  }
}

// Epics
export function fetchCategoriesEpic(action$: Observable<LocalValuesAction>): Observable<LocalValuesAction> {
  return action$.pipe(
    ofType(LOCAL_VALUES_FETCH_CATEGORIES_REQUEST),
    mergeMap(() => getItemCategoriesApi().pipe(
      map((response: Category[]) => {
        // note: mockup provisional
        //return fetchCategoriesSuccess(response)
        return fetchCategoriesSuccess(response);
      }),
      catchError((e: HTTPError) => of(fetchCategoriesError(e))))
    ));
}

export function fetchCountriesEpic(action$: Observable<LocalValuesAction>): Observable<LocalValuesAction> {
  return action$.pipe(
    ofType(LOCAL_VALUES_FETCH_COUNTRIES_REQUEST),
    mergeMap(() => getCountriesApi().pipe(
      map((response: CountryFull[]) => fetchCountriesSuccess(response)),
      catchError((e: HTTPError) => of(fetchCountriesError(e))))
    ));
}

export function fetchStoreCategoriesEpic(action$: Observable<LocalValuesAction>): Observable<LocalValuesAction> {
  return action$.pipe(
    ofType(LOCAL_VALUES_FETCH_STORE_CATEGORIES_REQUEST),
    mergeMap(() => getStoreCategoriesApi().pipe(
      map((response: Category[]) => fetchStoreCategoriesSuccess(response)),
      catchError((e: HTTPError) => of(fetchStoreCategoriesError(e))))
    ));
}

export function fetchOnlineStoresEpic(action$: Observable<LocalValuesAction>): Observable<LocalValuesAction> {
  return action$.pipe(
    ofType(LOCAL_VALUES_ONLINE_STORES_REQUEST),
    mergeMap(() => getOnlineStores().pipe(
      map((response: OnlineStore[]) => fetchOnlineStoresSuccess(response)),
      catchError((e: HTTPError) => of(fetchOnlineStoresErrorFn(e))))
    ));
}



// WEBPACK FOOTER //
// ./src/modules/local-values/duck.js